import { ChangeEvent, useRef, useImperativeHandle, forwardRef } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useFormField, TextField, Button, TextLink } from '@frontend/design-system';
import { MAX_KEYWORD_LENGTH, MIN_KEYWORD_LENGTH } from '../../constants';
import { useKeywordSearchStore } from '../../providers/keyword-search.provider/keyword-search.provider';

interface SearchBarProps {
  className?: string;
}

export interface SearchBarRef {
  focus: () => void;
}

export const SearchBar = forwardRef<SearchBarRef, SearchBarProps>(({ className = '' }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation('team-chat');
  const { keyword, clearSearch, startSearch } = useKeywordSearchStore(['keyword', 'clearSearch', 'startSearch']);

  const searchFieldProps = useFormField({
    type: 'text',
    placeholder: t('Search any messages or keywords'),
  });

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current && document.activeElement !== inputRef.current) {
        inputRef?.current?.focus();
      }
    },
  }));

  const isButtonDisabled =
    searchFieldProps.value.trim().length < MIN_KEYWORD_LENGTH ||
    searchFieldProps.value.trim().length > MAX_KEYWORD_LENGTH ||
    searchFieldProps.value.trim() === keyword;

  const onClickSearch = () => startSearch(searchFieldProps.value.trim());

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isButtonDisabled) {
      onClickSearch();
    }
  };

  const onClearAction = () => {
    searchFieldProps.onChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
    clearSearch();
  };

  return (
    <TextField
      label=''
      name='Keyword-search'
      minLength={MIN_KEYWORD_LENGTH}
      maxLength={MAX_KEYWORD_LENGTH}
      endAdornment={
        <span css={styles.endAdornment}>
          {(searchFieldProps.value.trim().length > 0 || keyword.length > 0) && (
            <TextLink onClick={onClearAction} trackingId='team-chat-2.0-keyword-search-clear-button'>
              {t('Clear')}
            </TextLink>
          )}
          <Button
            iconName='search'
            size='large'
            className='search'
            onClick={onClickSearch}
            disabled={isButtonDisabled}
            trackingId='team-chat-2.0-keyword-search-cta'
            hoverLabel={t('Search')}
          />
        </span>
      }
      css={styles.container}
      containerCss={styles.field}
      className={`${className} field-wrapper`}
      onKeyDown={onKeyDown}
      fieldComponentProps={{ ref: inputRef }}
      {...searchFieldProps}
    />
  );
});

SearchBar.displayName = 'SearchBar';

const styles = {
  field: css({
    width: '100%',

    '.field-wrapper': {
      padding: theme.spacing(0, 0, 0, 2),
      margin: 0,
    },
  }),
  container: css({ width: '100%' }),
  endAdornment: css({
    display: 'flex',
    gap: theme.spacing(2),

    '.search': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      // Generally the height is 40px but that escapes out of the field in the UI, that is why we have set it to 38px
      height: `38px`,
    },
  }),
};
